/**
 * MetricCard Component - Reusable metric display card
 * Consistent with billing app design patterns but optimized for admin analytics
 */

import React from 'react';
import {
  Card,
  Box,
  Text,
  InlineStack,
  BlockStack,
  Spinner,
  Badge,
  Tooltip,
  Icon,
} from '@shopify/polaris';
import {
  ArrowUpIcon,
  ArrowDownIcon,
  InfoIcon,
} from '@shopify/polaris-icons';
import apiClient from '../../utils/api';

export default function MetricCard({
  title,
  value,
  change,
  icon: IconComponent,
  color = 'default',
  loading = false,
  error = null,
  tooltip,
  subtitle,
  badge,
  className = '',
  formatter,
  format = 'number',
  /**
   * Inner padding for the tile. NO DEFAULT, deliberately.
   *
   * These three Boxes used to pass `padding="300"` alongside a `style` prop, and a style prop
   * on Polaris Box REPLACES the generated style attribute outright — so the 300 never reached
   * the page and every tile has only ever had the Card's own padding. The mechanism is fixed
   * (the flex column those styles were for is now a BlockStack, which does not fight the
   * padding), but honouring that 300 would have made the Analytics page and the brand
   * Configuration tab roomier than they have ever rendered.
   *
   * So the property is real and opt-in: a consumer that passes nothing gets a Box with no
   * padding, which is byte-identical to what it rendered before. Only the Dashboard passes a
   * value, because the Dashboard is the screen that asked to be compacted.
   */
  padding,
  ...props
}) {
  // Validate props to prevent crashes
  if (!title || (typeof title !== 'string' && typeof title !== 'number')) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('MetricCard: Invalid title prop provided:', title);
    }
    return null;
  }
  // Loading state with skeleton pattern matching billing app
  if (loading) {
    return (
      <Card {...props}>
        <Box padding={padding}>
          <BlockStack gap="050">
            <InlineStack align="space-between" blockAlign="center">
              <Text variant="bodySm" tone="subdued">
                {title}
              </Text>
              {IconComponent && (
                <Icon source={IconComponent} />
              )}
            </InlineStack>

            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <InlineStack gap="200" blockAlign="center">
                <Spinner size="small" />
                <Text variant="bodySm" tone="subdued">Loading...</Text>
              </InlineStack>
              {subtitle && (
                <Box padding="100" background="surface-neutral-subdued" borderRadius="200" />
              )}
            </div>

            {change && (
              <div style={{ height: '32px' }}>
                {/* Reserved to match the loaded card, so it does not resize when data arrives. */}
              </div>
            )}
          </BlockStack>
        </Box>
      </Card>
    );
  }

  // Error state
  if (error) {
    return (
      <Card {...props}>
        <Box padding={padding}>
          <BlockStack gap="050">
            <InlineStack align="space-between" blockAlign="center">
              <Text variant="bodySm" tone="subdued">
                {title}
              </Text>
              <Tooltip content="Unable to load data">
                <Icon source={InfoIcon} tone="critical" />
              </Tooltip>
            </InlineStack>

            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <Text variant="headingLg" tone="critical">
                --
              </Text>
              <Text variant="bodySm" tone="critical">
                Error loading data
              </Text>
            </div>

            {change && (
              <div style={{ height: '32px' }}>
                {/* Reserved to match the loaded card, so it does not resize when data arrives. */}
              </div>
            )}
          </BlockStack>
        </Box>
      </Card>
    );
  }

  // Format value based on the DECLARED format, never on the magnitude of the value.
  //
  // This used to read `if (val > 100 || val < -100) return formatCurrency(val)` — the
  // guess that "a big number must be money". So an order COUNT of 250 rendered as
  // "$250.00" while a count of 99 rendered correctly, and the card silently changed
  // units as the business grew. A count is not money at any magnitude, so magnitude
  // cannot be the input to this decision.
  //
  // The unit is now the call site's to state: `format="currency"` for money, or a
  // `formatter` of its own (which wins outright, and is what most money cards already
  // pass). The default is 'number', because a count shown as a plain number is at worst
  // unstyled, whereas money shown as a count — or a count shown as money — is wrong.
  const formatValue = (val) => {
    if (typeof val === 'number' && !isNaN(val) && isFinite(val)) {
      try {
        if (format === 'currency') {
          return apiClient.formatCurrency(val);
        }
        return new Intl.NumberFormat('en-US').format(val);
      } catch (error) {
        return '0';
      }
    }
    // Handle invalid numbers
    if (typeof val === 'number' && (isNaN(val) || !isFinite(val))) {
      return '--';
    }
    return val || '--';
  };

  // Determine color theme.
  //
  // Cards stay white. Colour is carried by the VALUE and the badge, never by the card
  // surface — a wall of tinted panels makes every metric look like a status, and the
  // eye stops being able to pick out the one that actually needs attention. This
  // matches the agreed wireframe, where the only coloured surfaces are alerts.
  const getColorTheme = () => {
    switch (color) {
      case 'success':
        return { cardTone: null, valueTone: 'success', badgeTone: 'success' };
      case 'warning':
        return { cardTone: null, valueTone: 'warning', badgeTone: 'warning' };
      case 'critical':
        return { cardTone: null, valueTone: 'critical', badgeTone: 'critical' };
      default:
        return { cardTone: null, valueTone: 'base', badgeTone: 'info' };
    }
  };

  const colorTheme = getColorTheme();

  // Only pass `background` when there IS a tone. background={null} overrides Polaris'
  // default surface token, so the card loses its white fill and reads grey against the
  // page instead of standing out from it — which is what inverted the whole dashboard.

  // Render change indicator (matching billing app pattern)
  const renderChange = () => {
    if (!change) return null;

    const isPositive = change.direction === 'up';
    const isNeutral = change.percentage === 0 || change.direction === 'neutral';
    
    if (isNeutral) {
      return (
        <Text variant="bodySm" tone="subdued">
          No change {change.period && `from ${change.period}`}
        </Text>
      );
    }

    return (
      <InlineStack gap="100" blockAlign="center">
        <Icon
          source={isPositive ? ArrowUpIcon : ArrowDownIcon}
          tone={isPositive ? 'success' : 'critical'}
        />
        <Text
          variant="bodySm"
          tone={isPositive ? 'success' : 'critical'}
          fontWeight="medium"
        >
          {(() => {
            const percentage = change.percentage;
            if (typeof percentage === 'number' && !isNaN(percentage) && isFinite(percentage)) {
              return Math.abs(percentage).toFixed(1) + '%';
            }
            return '0%';
          })()}
        </Text>
        {change.period && (
          <Text variant="bodySm" tone="subdued">
            {change.period}
          </Text>
        )}
        {change.isNew && (
          <Badge tone="info" size="small">
            New
          </Badge>
        )}
      </InlineStack>
    );
  };

  return (
    <Card {...props} {...(colorTheme.cardTone ? { background: colorTheme.cardTone } : {})} style={{ flex: 1 }}>
      <Box padding={padding}>
          <BlockStack gap="050">
          {/* Header with title, tooltip, and icon */}
          <InlineStack align="space-between" blockAlign="center">
            <InlineStack gap="100" blockAlign="center">
              <Text variant="bodySm" tone="subdued">
                {title}
              </Text>
              {tooltip && (
                <Tooltip content={tooltip} preferredPosition="above">
                  <Box paddingInlineStart="100">
                    <Icon source={InfoIcon} tone="subdued" />
                  </Box>
                </Tooltip>
              )}
              {badge && (
                <Badge tone={colorTheme.badgeTone} size="small">
                  {badge}
                </Badge>
              )}
            </InlineStack>

            {IconComponent && (
              <Box>
                <Icon source={IconComponent} tone={colorTheme.valueTone} />
              </Box>
            )}
          </InlineStack>

          {/* Main value - flex grow to take available space */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <Text
              variant="heading2xl"
              tone={colorTheme.valueTone}
              fontWeight="semibold"
            >
              {(() => {
                try {
                  return formatter ? formatter(value) : formatValue(value);
                } catch (error) {
                  if (process.env.NODE_ENV === 'development') {
                    console.error('MetricCard: Error formatting value:', error, { value, formatter });
                  }
                  return '--';
                }
              })()}
            </Text>

            {subtitle && (
              <Text variant="bodySm" tone="subdued">
                {subtitle}
              </Text>
            )}
          </div>

          {/* Change indicator, rendered only when there IS a change to show.
              
              This div was unconditionally 32px tall, and renderChange() returns null without a
              `change` prop — which none of the brand or global analytics cards pass. So every
              card carried 32px of empty space under its value, which is the "too much bottom
              padding" on those grids. The height is still fixed when a change exists, so a mix
              of cards still lines up; Grid.Cell already stretches them to equal height anyway. */}
          {change && (
            <div style={{ height: '32px', display: 'flex', alignItems: 'center' }}>
              {renderChange()}
            </div>
          )}
        </BlockStack>
      </Box>
    </Card>
  );
}

// Preset metric card variants for common use cases.
//
// `format` is set here as well as at the call sites: Revenue and Commission cards are
// money by definition, Orders is a count by definition, so the preset carries the unit
// and a call site that forgets to state it still cannot render the wrong one. Both sit
// before {...props} so an individual card can still override.
export const RevenueCard = (props) => (
  <MetricCard
    color="success"
    format="currency"
    tooltip={props.tooltip || "Total revenue generated from completed orders across all selected brands"}
    {...props}
  />
);

export const CommissionCard = (props) => (
  <MetricCard
    color="default"
    format="currency"
    tooltip={props.tooltip || "Total commission earned based on configured rates and fulfilled order values +  Adjustments configured by the admin"}
    {...props}
  />
);

export const OrdersCard = (props) => (
  <MetricCard
    color="info"
    format="number"
    tooltip={props.tooltip || "Total number of orders fulfilled in the selected time period"}
    {...props}
  />
);

export const GrowthCard = (props) => (
  <MetricCard
    color={props.change?.direction === 'up' ? 'success' : 'critical'}
    tooltip="Growth compared to previous period"
    {...props}
  />
);